import { Card, CardHeader, CardTitle, CardContent, Skeleton } from '@/components/ui'
import { useOutletContext } from 'react-router-dom'
import { Users, Activity, Key, Webhook } from 'lucide-react'
import { type AppFullInfo } from '@/api/client'
import { type AppStats } from './index'

interface AppContext {
  app: AppFullInfo
  setApp: (app: AppFullInfo) => void
  stats: AppStats | null
}

export function AppOverviewPage() {
  const context = useOutletContext<AppContext | null>()
  const app = context?.app
  const stats = context?.stats

  if (!app) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="py-6">
              <Skeleton className="h-10 w-10 rounded-lg mb-2" />
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-4 w-24 mt-1" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  const statCards = [
    { icon: Users, label: 'Users', value: stats?.authorization_count ?? 0 },
    { icon: Activity, label: 'Requests (24h)', value: 0 }, // TODO: Wire up analytics
    { icon: Key, label: 'Scopes', value: stats?.scope_count ?? 0 },
    { icon: Webhook, label: 'Webhooks', value: stats?.webhook_count ?? 0 },
  ]

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <Card key={stat.label}>
            <CardContent className="py-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--bg-elevated-2)] mb-2">
                <stat.icon className="h-5 w-5 text-[var(--text-secondary)]" />
              </div>
              <p className="text-2xl font-semibold text-[var(--text-primary)]">{stat.value}</p>
              <p className="text-sm text-[var(--text-muted)]">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>App Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="text-sm text-[var(--text-muted)]">App ID</p>
            <code className="text-sm font-mono text-[var(--text-primary)]">{app.id}</code>
          </div>
          <div>
            <p className="text-sm text-[var(--text-muted)]">Status</p>
            <p className="text-sm text-[var(--text-primary)]">{app.status}</p>
          </div>
          <div>
            <p className="text-sm text-[var(--text-muted)]">Created</p>
            <p className="text-sm text-[var(--text-primary)]">{new Date(app.created_at).toLocaleDateString()}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
